import { Button, Select, Table, TextField } from "@op/sf-editor";
import { FeatherPlus, FeatherSearch } from "@subframe/core";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { UserTableRow } from "../../../components/app/admin/users/UserTableRow";
import { UserChangeDialog } from "../../../components/app/admin/users/UserChangeDialog";
import { Pagination } from "../../../components/Pagination";
import { UserDeleteDialog } from "../../../components/app/admin/users/UserDeleteDialog";
import { UserCreateDialog } from "../../../components/app/admin/users/UserCreateDialog";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { adminRouterClient } from "../../../lib/adminRouterClient";
import { useDebounce } from "use-debounce";

const RouteComponent = () => {
  const [createUserDialogOpen, setCreateUserDialogOpen] =
    useState<boolean>(false);
  const [changeUserDialogId, setChangeUserDialogId] = useState<string | null>(
    null
  );
  const [deleteUserDialogId, setDeleteUserDialogId] = useState<string | null>(
    null
  );

  const [searchField, setSearchField] = useState<"name" | "email">("name");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [limit, setLimit] = useState<number>(25);
  const [offset, setOffset] = useState<number>(0);
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);

  const queryClient = useQueryClient();

  const { data: queryData } = useQuery(
    {
      queryKey: [
        "admin-users",
        { limit, offset, searchField, debouncedSearchTerm },
      ],
      queryFn: async () => {
        const res = await adminRouterClient.users.$get({
          query: {
            limit: limit.toString(),
            offset: offset.toString(),
            searchField,
            searchTerm: debouncedSearchTerm,
          },
        });
        return await res.json();
      },
    },
    queryClient
  );
  const { docs: data, length, total } = queryData ?? {};

  const userToChange = useMemo(() => {
    if (!changeUserDialogId) return null;
    const user = data?.find((user) => user.id === changeUserDialogId);
    if (!user) return null;
    return {
      ...user,
      id: user.id!,
      createdAt: new Date(user?.createdAt ?? ""),
      updatedAt: new Date(user?.updatedAt ?? ""),
    };
  }, [changeUserDialogId]);

  const userToDelete = useMemo(() => {
    if (!deleteUserDialogId) return null;
    const user = data?.find((user) => user.id === deleteUserDialogId);
    if (!user) return null;
    return {
      ...user,
      id: user.id!,
      createdAt: new Date(user?.createdAt ?? ""),
      updatedAt: new Date(user?.updatedAt ?? ""),
    };
  }, [deleteUserDialogId]);

  // Note(Chris): Dumb fix because @radix-ui/dialog is buggy: https://github.com/radix-ui/primitives/issues/3645
  useEffect(() => {
    if (changeUserDialogId !== null) return;
    setTimeout(() => {
      document.body.style.pointerEvents = "auto";
    }, 1);
  }, [changeUserDialogId]);
  useEffect(() => {
    if (deleteUserDialogId !== null) return;
    setTimeout(() => {
      document.body.style.pointerEvents = "auto";
    }, 1);
  }, [deleteUserDialogId]);

  return (
    <>
      <div className="container max-w-none flex h-full w-full flex-col items-start gap-12 bg-default-background py-12">
        <div className="flex w-full flex-col items-start gap-4">
          <div className="flex w-full items-center gap-6 justify-between">
            <span className="whitespace-pre-wrap text-heading-1 font-heading-1 text-default-font">
              {"Users"}
            </span>
            <div className="flex items-center gap-2">
              <Select
                disabled={false}
                error={false}
                variant="outline"
                label=""
                placeholder="Select"
                helpText=""
                icon={null}
                value={searchField}
                onValueChange={(value: string) => {
                  setSearchField(value as "name" | "email");
                }}
              >
                <Select.Item value="name">Name</Select.Item>
                <Select.Item value="email">Email</Select.Item>
              </Select>
              <TextField
                disabled={false}
                error={false}
                variant="outline"
                label=""
                helpText=""
                icon={null}
                iconRight={<FeatherSearch />}
              >
                <TextField.Input
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setSearchTerm(event.target.value);
                  }}
                />
              </TextField>
            </div>
          </div>
          <div className="flex w-full flex-col items-start">
            <div className="flex w-full flex-col items-end justify-center gap-6">
              <Button
                disabled={false}
                variant="brand-secondary"
                size="small"
                icon={null}
                iconRight={<FeatherPlus />}
                loading={false}
                onClick={() => setCreateUserDialogOpen(true)}
              >
                Create User
              </Button>
            </div>
            <div className="flex w-full flex-col items-start gap-4">
              <Table
                header={
                  <Table.HeaderRow>
                    <Table.HeaderCell>Name</Table.HeaderCell>
                    <Table.HeaderCell>Email</Table.HeaderCell>
                    <Table.HeaderCell>Role</Table.HeaderCell>
                  </Table.HeaderRow>
                }
              >
                {data?.map((user) => (
                  <UserTableRow
                    key={user.id}
                    id={user.id}
                    name={user.name}
                    email={user.email}
                    role={user.role}
                    onChangeOpen={() => setChangeUserDialogId(user.id)}
                    onDeleteOpen={() => setDeleteUserDialogId(user.id)}
                  />
                ))}
              </Table>
              <Pagination
                limit={limit}
                onLimitChange={setLimit}
                offset={offset}
                length={length ?? 0}
                total={total ?? 0}
                onOffsetChange={setOffset}
              />
            </div>
          </div>
        </div>
      </div>
      <UserCreateDialog
        open={createUserDialogOpen}
        onOpenChange={setCreateUserDialogOpen}
      />
      {userToChange && (
        <UserChangeDialog
          user={userToChange ?? null}
          open={!!changeUserDialogId}
          onOpenChange={() => setChangeUserDialogId(null)}
        />
      )}
      {userToDelete && (
        <UserDeleteDialog
          user={userToDelete ?? null}
          open={!!deleteUserDialogId}
          onOpenChange={() => setDeleteUserDialogId(null)}
        />
      )}
    </>
  );
};

export const Route = createFileRoute("/admin/users/")({
  component: RouteComponent,
});
